/**
 * The seven reasons offered on the cancellation flow's first step.
 *
 * Values must stay in sync with app/Enum/SubscriptionCancellationReason.php — they are
 * posted verbatim to the reason-selected tracking endpoint and, later in the flow, to
 * the cancel endpoint. Labels come from Figma.
 */
export const CANCELLATION_REASONS = [
	{ value: 'got_job', label: 'I got a job' },
	{ value: 'too_expensive', label: 'Premium plan is too expensive' },
	{ value: 'no_relevant_jobs', label: "Didn't find relevant jobs" },
	{ value: 'pausing_search', label: 'Pausing my job search' },
	{ value: 'missing_features', label: 'Missing features I expected' },
	{ value: 'technical_issues', label: 'Technical issues' },
	{ value: 'other', label: 'Other' },
];

export const REASON_STEP = 'reason';
export const CELEBRATION_STEP = 'celebration';
export const OFFER_STEP = 'offer';
export const DISCOUNT_APPLIED_STEP = 'discount-applied';
export const BRIDGE_STEP = 'bridge';

/**
 * "Here is what changes on Free" — the user's most-used features against their Free-plan caps,
 * shown before the offer rather than after it. The three reasons that open here share it: what
 * they are giving up is the same screen whatever prompted the cancellation.
 */
export const USAGE_IMPACT_STEP = 'usage-impact';

/**
 * The feedback screen, reached by every branch that ends in a cancellation the product could
 * not talk the user out of. The branches share the screen but not its copy: each one brings its
 * own heading, sub-copy and placeholder from the maps below.
 */
export const FEEDBACK_STEP = 'feedback';

/**
 * Every branch ends here once the cancellation goes through.
 */
export const CONFIRMATION_STEP = 'confirmation';

/**
 * The step each reason routes to once it is picked. `pausing_search` has no retention screen
 * of its own: its reassurance and its feedback ask share one screen, which is the shared
 * feedback step wearing the branch's copy. The last three open on the usage-impact screen and
 * only reach the offer if they choose to go on cancelling.
 */
export const BRANCH_ENTRY_STEP = {
	got_job: CELEBRATION_STEP,
	too_expensive: OFFER_STEP,
	no_relevant_jobs: BRIDGE_STEP,
	pausing_search: FEEDBACK_STEP,
	missing_features: USAGE_IMPACT_STEP,
	technical_issues: USAGE_IMPACT_STEP,
	other: USAGE_IMPACT_STEP,
};

/**
 * The warm closing line the confirmation step adds, per branch. Only the branches that
 * part on good terms get one; the branches that cancel over an unmet expectation do not.
 */
export const ENCOURAGEMENT_BY_REASON = {
	got_job: 'We will be happy to see you again when you’ll plan your next career leap.',
	pausing_search: 'Do not give up on the search. Whenever you need us again, we will be here to help.',
};

/**
 * The feedback step's heading, per branch. Every reason that reaches the feedback step has an
 * entry, because the step has no shared fallback: a reason missing here would render with no
 * heading at all.
 */
export const FEEDBACK_TITLE_BY_REASON = {
	too_expensive: 'Help us get the price right',
	no_relevant_jobs: 'Sorry the jobs missed the mark',
	pausing_search: 'Take the time you need',
	missing_features: 'Tell us what we’re missing',
	technical_issues: 'Sorry for the trouble',
	other: 'Help us understand',
};

/**
 * The feedback step's sub-copy, per branch, on the same every-reason terms as the heading. A
 * branch can land here with or without having seen its offer or bridge, so none of it refers
 * to one.
 */
export const FEEDBACK_DESCRIPTION_BY_REASON = {
	too_expensive: 'Would you mind sharing a bit more? We want Premium to be worth what you pay for it, '
		+ 'and we can’t get there without your feedback.',
	no_relevant_jobs: 'Would you mind sharing a bit more? Knowing the roles you were after helps us improve '
		+ 'the matches we show, and we can’t do that without your feedback.',
	pausing_search: 'Would you mind providing more details about your situation? Our main goal is '
		+ 'to help job seekers land their dream jobs faster, and we can’t do that without your feedback.',
	missing_features: 'Would you mind sharing a bit more? What we build next comes from feedback like yours, '
		+ 'so the details really help.',
	technical_issues: 'Would you mind sharing a bit more, like what you were doing when it happened? '
		+ 'The details help us find and fix the problem faster.',
	other: 'Would you mind telling us what led to your decision? Every answer helps us build a better '
		+ 'WonsultingAI for job seekers like you.',
};

/**
 * The feedback step's placeholder, per branch. Each branch asks about the thing its own
 * reason implies, so the shell reads the copy from here rather than knowing the branch.
 */
export const FEEDBACK_PLACEHOLDER_BY_REASON = {
	too_expensive: 'What would make the price feel worth it?',
	no_relevant_jobs: 'What kind of roles were you hoping to see?',
	pausing_search: 'What was the main reason for your decision?',
	missing_features: 'What were you expecting that we do not have?',
	technical_issues: 'What went wrong, exactly?',
	other: 'Tell us a bit more.',
};
